// packages/i18n/src/embedMessageSubset.ts
//
// The ICU message syntax the embed's widgets format (docs/embed-size-and-speed.md §3.2, icu-stack): text with ICU's
// apostrophe quoting, `{name}`, a cardinal `{name, plural, …}` with `#`, `=N` selectors and `offset:`, and
// `{name, select, …}`, nested to any depth. Every string a widget fetches uses only these, in all 13 locales, and
// scripts/i18n/checks/embedMessageSubset.ts keeps it so (`pnpm i18n:check`, and the embed deploy's own check).
//
// `parseEmbedMessage` is a port of @formatjs/icu-messageformat-parser 3.5.1 (use-intl 4.8.2's parser, through
// intl-messageformat 11.1.2) for exactly those constructs: a message it accepts gets the elements formatjs gives
// it, and a message formatjs refuses it refuses too. It also refuses what formatjs accepts outside the subset
// (`selectordinal`, `number`, `date`, `time`, tags), so `embedFormatMessage.ts`, the embed's stand-in for
// use-intl's formatter, fails exactly where it could not format as use-intl would.
//
// No imports: the embed deploy workflow's check (scripts/i18n/checkEmbed.ts) may load first-party code only.

/** `#` inside a plural option: the plural's number, after its offset. */
export const POUND = 0;

/** A `{name}` argument. */
export interface EmbedMessageArgument {
	type: 'argument';
	name: string;
}

/** A `{name, plural, offset:N =M {…} one {…} other {…}}` argument. */
export interface EmbedMessagePlural {
	type: 'plural';
	name: string;
	/** `offset:N`, else 0 (formatjs's `offset`). */
	offset: number;
	/** Each option's elements by its selector, `=N` spelt as written (`=1`, `=+1`). A null-prototype object. */
	options: Record<string, EmbedMessageElement[]>;
}

/** A `{name, select, a {…} other {…}}` argument. */
export interface EmbedMessageSelect {
	type: 'select';
	name: string;
	/** Each option's elements by its selector. A null-prototype object. */
	options: Record<string, EmbedMessageElement[]>;
}

/** Text (quotes resolved), `#`, or an argument: formatjs's literal, pound, argument, plural and select elements. */
export type EmbedMessageElement = string | typeof POUND | EmbedMessageArgument | EmbedMessagePlural | EmbedMessageSelect;

/** Constructs formatjs parses that the subset does not format. */
export type EmbedUnsupportedConstruct = 'selectordinal' | 'number' | 'date' | 'time' | 'tag';

/**
 * A message the subset does not format: invalid ICU (formatjs refuses it as well), or valid ICU outside the subset.
 * Its message is only the construct (or "invalid") and the offset; whoever shows it words it (the subset check, and
 * the development build's errors), so the widgets' production bundles carry no sentences for it.
 */
export class EmbedMessageSyntaxError extends Error {
	constructor(
		/** The construct outside the subset, or `null` when the message is not valid ICU at all. */
		readonly construct: EmbedUnsupportedConstruct | null,
		/** Where parsing stopped, in UTF-16 code units. */
		readonly offset: number,
	) {
		super(`${construct ?? 'invalid'} at ${offset}`);
		this.name = 'EmbedMessageSyntaxError';
	}
}

/** The argument whose options a run of text is in: formatjs's `parentArgType`. */
type Parent = 'plural' | 'select' | '';

const APOSTROPHE = 39;
const LEFT_BRACE = 123;
const RIGHT_BRACE = 125;
const HASH = 35;
const LESS_THAN = 60;
const GREATER_THAN = 62;
const SLASH = 47;
const COMMA = 44;
const COLON = 58;
const EQUALS = 61;
const PLUS = 43;
const MINUS = 45;

/**
 * formatjs's identifier: anything but White_Space and Pattern_Syntax. Built from a string, as formatjs builds it, so
 * Babel leaves the Unicode property escapes to the browser instead of spelling them out as character classes.
 */
const IDENTIFIER = new RegExp('([^\\p{White_Space}\\p{Pattern_Syntax}]*)', 'yu');

/** formatjs's `_isWhiteSpace`: Pattern_White_Space, not the identifier's White_Space (a no-break space ends a name but is no space). */
function isPatternWhiteSpace(c: number): boolean {
	return (c >= 9 && c <= 13) || c === 32 || c === 133 || (c >= 8206 && c <= 8207) || c === 8232 || c === 8233;
}

/** formatjs's `_isAlpha`: an ASCII letter, which is what opens a tag. */
function isAsciiLetter(c: number): boolean {
	return (c >= 97 && c <= 122) || (c >= 65 && c <= 90);
}

/** The elements of `message`; throws {@link EmbedMessageSyntaxError} for anything the subset does not format. */
export function parseEmbedMessage(message: string): EmbedMessageElement[] {
	let i = 0;

	const fail = (construct: EmbedUnsupportedConstruct | null = null): never => {
		throw new EmbedMessageSyntaxError(construct, i);
	};
	const code = (at: number): number => message.charCodeAt(at); // NaN past the end, like formatjs's peek()
	const skipSpace = (): void => {
		while (i < message.length && isPatternWhiteSpace(code(i))) i++;
	};
	const identifier = (): string => {
		IDENTIFIER.lastIndex = i;
		const name = IDENTIFIER.exec(message)?.[1] ?? '';
		i += name.length;
		return name;
	};
	/** formatjs's `tryParseDecimalInteger`: an optional sign, then digits, making a safe integer. */
	const decimalInteger = (): number => {
		let sign = 1;
		if (code(i) === PLUS) i++;
		else if (code(i) === MINUS) {
			sign = -1;
			i++;
		}
		let digits = false;
		let value = 0;
		while (i < message.length && code(i) >= 48 && code(i) <= 57) {
			digits = true;
			value = value * 10 + (code(i) - 48);
			i++;
		}
		if (!digits) fail();
		value *= sign;
		if (!Number.isSafeInteger(value)) fail();
		return value;
	};

	/** formatjs's `parseMessage`; `depth` is its nesting level. */
	function parseElements(depth: number, parent: Parent): EmbedMessageElement[] {
		const elements: EmbedMessageElement[] = [];
		while (i < message.length) {
			const c = code(i);
			if (c === LEFT_BRACE) elements.push(parseArgument(depth));
			else if (c === RIGHT_BRACE && depth > 0) break;
			else if (c === HASH && parent === 'plural') {
				i++;
				elements.push(POUND);
			} else if (c === LESS_THAN && code(i + 1) === SLASH) fail(); // a closing tag with no tag open
			else if (c === LESS_THAN && isAsciiLetter(code(i + 1))) fail('tag');
			else elements.push(parseLiteral(depth, parent));
		}
		return elements;
	}

	/** formatjs's `parseLiteral`: quoted text first, then any character that starts nothing, then a `<` that opens no tag. */
	function parseLiteral(depth: number, parent: Parent): string {
		let text = '';
		for (;;) {
			const c = code(i);
			if (c === APOSTROPHE) {
				const next = code(i + 1);
				if (next === APOSTROPHE) {
					text += "'";
					i += 2;
					continue;
				}
				if (
					next === LEFT_BRACE ||
					next === LESS_THAN ||
					next === GREATER_THAN ||
					next === RIGHT_BRACE ||
					(next === HASH && parent === 'plural')
				) {
					// A quoted run: the character it escapes, then everything up to the closing apostrophe or the end; a
					// doubled apostrophe inside it is one.
					text += message[i + 1];
					i += 2;
					while (i < message.length) {
						if (code(i) === APOSTROPHE) {
							if (code(i + 1) !== APOSTROPHE) {
								i++;
								break;
							}
							i++;
						}
						text += message[i];
						i++;
					}
					continue;
				}
			}
			if (i < message.length && !(c === LESS_THAN || c === LEFT_BRACE || (c === HASH && parent === 'plural') || (c === RIGHT_BRACE && depth > 0))) {
				text += message[i];
				i++;
				continue;
			}
			if (c === LESS_THAN && !(isAsciiLetter(code(i + 1)) || code(i + 1) === SLASH)) {
				text += '<';
				i++;
				continue;
			}
			return text;
		}
	}

	/** formatjs's `parseArgument` and, for `plural` and `select`, `parseArgumentOptions` and `tryParsePluralOrSelectOptions`. */
	function parseArgument(depth: number): EmbedMessageArgument | EmbedMessagePlural | EmbedMessageSelect {
		i++; // `{`
		skipSpace();
		if (i >= message.length || code(i) === RIGHT_BRACE) fail();
		const name = identifier();
		if (!name) fail();
		skipSpace();
		if (code(i) === RIGHT_BRACE) {
			i++;
			return { type: 'argument', name };
		}
		if (code(i) !== COMMA) fail();
		i++;
		skipSpace();
		const typeName = identifier();
		const type =
			typeName === 'plural' || typeName === 'select'
				? typeName
				: fail(typeName === 'selectordinal' || typeName === 'number' || typeName === 'date' || typeName === 'time' ? typeName : null);
		skipSpace();
		if (code(i) !== COMMA) fail();
		i++;
		skipSpace();
		let selector = identifier();
		let offset = 0;
		if (type === 'plural' && selector === 'offset') {
			if (code(i) !== COLON) fail();
			i++;
			skipSpace();
			offset = decimalInteger();
			skipSpace();
			selector = identifier();
		}
		const options = Object.create(null) as Record<string, EmbedMessageElement[]>;
		let hasOptions = false;
		for (;;) {
			if (!selector) {
				if (type !== 'plural' || code(i) !== EQUALS) break;
				const from = i;
				i++;
				decimalInteger();
				selector = message.slice(from, i);
			}
			if (selector in options) fail();
			skipSpace();
			if (code(i) !== LEFT_BRACE) fail();
			i++;
			const elements = parseElements(depth + 1, type);
			if (code(i) !== RIGHT_BRACE) fail();
			i++;
			options[selector] = elements;
			hasOptions = true;
			skipSpace();
			selector = identifier();
		}
		if (!hasOptions || !('other' in options) || code(i) !== RIGHT_BRACE) fail();
		i++;
		return type === 'plural' ? { type, name, offset, options } : { type, name, options };
	}

	return parseElements(0, '');
}
